import GridColumnHeader from '../components/GridColumnHeader';
import { processClientRows } from '../utility/filterHelper';
import { GRID_MODE } from '../const/dataGridConstants.js';
import React, { useCallback, useMemo } from 'react';

const useCommonDataGrid = ({
  setPageSize,
  setPage,
  searchInput,
  setSearchText,
  setColumnWidths,
  setLocalColumnSearch,
  setColumnOrder,
  checkboxSelection,
  initialPinnedColumns,
  hasActions,
  setPinnedColumns,
  setDensity,
  lastSyncedDensityRef,
  columns,
  columnWidths,
  disableColumnMenu,
  actionColumn,
  enableColumnSearch,
  reorderEnabled,
  localColumnSearch,
  columnOrder,
  rows,
  searchText,
  columnSearch,
  columnVisibilityModel,
  mode,
  toolbar,
  setSearchInput,
  search,
  layoutSync,
  layoutSaving,
  layoutDirty,
  saveLayout,
  resetLayout,
  setReorderEnabled,
  setColumnVisibilityModel,
  exportActions,
}) => {
  const handlePageSizeChange = useCallback(
    (newPageSize) => {
      setPageSize(newPageSize);
      setPage(0);
    },
    [setPageSize, setPage]
  );

  const handleSearch = useCallback(
    (value = searchInput) => {
      setSearchText(value);
    },
    [searchInput, setSearchText]
  );

  const handleColumnWidthChange = useCallback(
    (params) => {
      setColumnWidths((prev) => ({ ...prev, [params.colDef.field]: params.width }));
    },
    [setColumnWidths]
  );

  const handleColumnSearchChange = useCallback(
    (field, value) => {
      setLocalColumnSearch((prev) => ({ ...prev, [field]: value }));
    },
    [setLocalColumnSearch]
  );

  const handlePinnedColumnsChange = useCallback(
    (newPinnedColumns) => {
      const defaultRight = initialPinnedColumns?.right || [];
      const userPinnedRight = (newPinnedColumns.right || []).filter(
        (field) => !defaultRight.includes(field) && field !== 'actions'
      );
      const right = [...userPinnedRight, ...defaultRight.filter((field) => field !== 'actions')];

      if (hasActions && !right.includes('actions')) {
        right.push('actions');
      }

      setPinnedColumns({ ...newPinnedColumns, right });
    },
    [initialPinnedColumns, hasActions, setPinnedColumns]
  );

  const handleStateChange = useCallback(
    (state) => {
      const newDensity = state?.density?.value;

      if (newDensity && newDensity !== lastSyncedDensityRef.current) {
        lastSyncedDensityRef.current = newDensity;
        setDensity(newDensity);
      }
    },
    [setDensity, lastSyncedDensityRef]
  );

  const finalColumns = useMemo(() => {
    const visibleColumns = columns
      .filter((column) => column.internal !== true)
      .map((column) => ({
        ...column,
        width: columnWidths[column.field] ?? column.width,
        flex:
          columnWidths[column.field] != null ? 0 : column.flex ?? (column.width == null ? 1 : 0),
        fixed: column.fixed,
        minWidth: column.minWidth ?? 100,
        align: column.align || 'center',
        headerAlign: column.headerAlign || 'center',
        sortable: column.sortable !== false,
        filterable: column.filterable !== false,
        hideable: column.hideable,
        disableColumnMenu: column.disableColumnMenu ?? disableColumnMenu,
        disableColumnSelector: column.disableColumnSelector,
      }));
    return hasActions ? [...visibleColumns, actionColumn] : visibleColumns;
  }, [columns, columnWidths, disableColumnMenu, hasActions, actionColumn]);

  const searchableColumns = useMemo(() => {
    return finalColumns.map((column) => {
      const isSearchable =
        enableColumnSearch && column.searchable !== false && column.field !== 'actions';
      const isDraggable = reorderEnabled && column.field !== 'actions';
      return {
        ...column,
        renderHeader: () =>
          React.createElement(GridColumnHeader, {
            isDraggable,
            column,
            enableColumnSearch,
            isSearchable,
            value: localColumnSearch[column.field] || '',
            onChange: (event) => handleColumnSearchChange(column.field, event.target.value),
          }),
      };
    });
  }, [
    finalColumns,
    enableColumnSearch,
    localColumnSearch,
    handleColumnSearchChange,
    reorderEnabled,
  ]);

  const orderedColumns = useMemo(() => {
    const order = Array.isArray(columnOrder) ? columnOrder : [];

    if (order.length === 0) {
      return searchableColumns;
    }

    const columnMap = new Map(searchableColumns.map((col) => [col.field, col]));
    const ordered = order.map((field) => columnMap.get(field)).filter(Boolean);
    const remaining = searchableColumns.filter((col) => !order.includes(col.field));
    return [...ordered, ...remaining];
  }, [searchableColumns, columnOrder]);

  const defaultPinnedColumns = useMemo(() => {
    const left = [...(initialPinnedColumns.left || [])];
    const right = [...(initialPinnedColumns.right || [])];

    if (checkboxSelection && !left.includes('__check__')) {
      left.unshift('__check__');
    }

    searchableColumns.forEach((col) => {
      if (col.fixed === 'left' && !left.includes(col.field)) {
        left.push(col.field);
      }

      if (col.fixed === 'right' && col.field !== 'actions' && !right.includes(col.field)) {
        right.push(col.field);
      }
    });

    if (hasActions && !right.includes('actions')) {
      right.push('actions');
    }

    return {
      left,
      right,
    };
  }, [searchableColumns, checkboxSelection, hasActions, initialPinnedColumns]);

  const processedRows = useMemo(
    () =>
      mode === GRID_MODE.SERVER
        ? rows
        : processClientRows({
            rows,
            searchText,
            columnSearch,
            columns: finalColumns.filter(
              (column) =>
                column.field !== 'actions' &&
                column.searchable !== false &&
                (columnVisibilityModel[column.field] ?? !column.hide)
            ),
          }),
    [rows, searchText, columnSearch, finalColumns, columnVisibilityModel, mode]
  );

  const defaultColumnVisibility = useMemo(
    () =>
      columns.reduce((acc, col) => {
        if (col.hide === true) {
          acc[col.field] = false;
        }

        return acc;
      }, {}),
    [columns]
  );

  const mergedColumnVisibilityModel = useMemo(
    () => ({ ...defaultColumnVisibility, ...columnVisibilityModel }),
    [defaultColumnVisibility, columnVisibilityModel]
  );

  const toolbarProps = useMemo(
    () => ({
      toolbar,
      searchInput,
      setSearchInput,
      onSearch: handleSearch,
      search,
      layoutSync: {
        enabled: layoutSync.enabled,
        saving: layoutSaving,
        isDirty: layoutDirty,
        saveLayout,
        resetLayout,
      },
      columnOrdering: {
        orderedColumns,
        setColumnOrder,
        reorderEnabled,
        toggleReorder: () => setReorderEnabled((prev) => !prev),
      },
      columnVisibility: {
        model: mergedColumnVisibilityModel,
        setModel: setColumnVisibilityModel,
      },
      exportActions,
    }),
    [
      toolbar,
      searchInput,
      setSearchInput,
      setReorderEnabled,
      handleSearch,
      search,
      layoutSync.enabled,
      layoutSaving,
      layoutDirty,
      saveLayout,
      resetLayout,
      orderedColumns,
      setColumnOrder,
      reorderEnabled,
      mergedColumnVisibilityModel,
      setColumnVisibilityModel,
      exportActions,
    ]
  );

  const handleColumnOrderChange = useCallback(
    (params) => {
      setColumnOrder((prev) => {
        const safePrev = Array.isArray(prev) ? prev : [];
        const currentOrder = safePrev.length ? safePrev : orderedColumns.map((c) => c.field);
        const newOrder = [...currentOrder];
        const fromIndex = newOrder.indexOf(params.field);
        if (fromIndex !== -1) newOrder.splice(fromIndex, 1);
        const adjustedTargetIndex = checkboxSelection
          ? Math.max(0, params.targetIndex - 1)
          : params.targetIndex;
        newOrder.splice(adjustedTargetIndex, 0, params.field);
        return newOrder;
      });
    },
    [orderedColumns, setColumnOrder, checkboxSelection]
  );

  return {
    handlePageSizeChange,
    handleSearch,
    handleColumnWidthChange,
    handleColumnSearchChange,
    handleColumnOrderChange,
    handlePinnedColumnsChange,
    handleStateChange,
    finalColumns,
    searchableColumns,
    orderedColumns,
    defaultPinnedColumns,
    processedRows,
    defaultColumnVisibility,
    mergedColumnVisibilityModel,
    toolbarProps,
  };
};

export default useCommonDataGrid;
